export const CONTACT_COMPLIANCE_SIGNAL_KEYS = [
  'never_solicited',
  'dnc_request',
  'repeat_contact_complaint',
] as const;

export type ContactComplianceSignal = (typeof CONTACT_COMPLIANCE_SIGNAL_KEYS)[number];
export type ContactComplianceFilter = 'any' | ContactComplianceSignal;

export interface ContactComplianceRecord {
  id?: string | number | null;
  Campaign?: string | null;
  never_solicited?: boolean | null;
  dnc_request?: boolean | null;
  repeat_contact_complaint?: boolean | null;
  repeat_contact_count?: number | null;
  contact_compliance_assessment_status?: string | null;
  contact_compliance_severity?: string | null;
  contact_compliance_confidence?: string | null;
  contact_compliance_evidence?: unknown;
  contact_compliance_action?: string | null;
  contact_compliance_review_status?: string | null;
  contact_compliance_action_status?: string | null;
  contact_compliance_scored_at?: string | null;
}

export const CONTACT_COMPLIANCE_SIGNAL_META: Record<
  ContactComplianceSignal,
  { label: string; shortLabel: string }
> = {
  never_solicited: {
    label: 'Never solicited',
    shortLabel: 'Never solicited',
  },
  dnc_request: {
    label: 'Stop calling / DNC',
    shortLabel: 'DNC',
  },
  repeat_contact_complaint: {
    label: 'Excessive repeat calls',
    shortLabel: 'Repeat calls',
  },
};

export function getActiveContactComplianceSignals(
  record: ContactComplianceRecord | null | undefined
): ContactComplianceSignal[] {
  if (!record) return [];
  return CONTACT_COMPLIANCE_SIGNAL_KEYS.filter((key) => record[key] === true);
}

export function hasContactComplianceSignal(
  record: ContactComplianceRecord | null | undefined
): boolean {
  return getActiveContactComplianceSignals(record).length > 0;
}

function evidenceText(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => evidenceText(entry))
      .filter((entry): entry is string => Boolean(entry));
    return parts.length > 0 ? parts.join(' ') : null;
  }
  if (!value || typeof value !== 'object') return null;

  const object = value as Record<string, unknown>;
  for (const key of ['summary', 'text', 'rationale', 'evidence', 'excerpt']) {
    const result = evidenceText(object[key]);
    if (result) return result;
  }
  return null;
}

export function getContactComplianceEvidence(
  record: ContactComplianceRecord | null | undefined,
  signal: ContactComplianceSignal
): string | null {
  const evidence = record?.contact_compliance_evidence;
  if (!evidence) return null;

  if (Array.isArray(evidence)) {
    const matchingItems = evidence.filter((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
      const entry = item as Record<string, unknown>;
      const tag = entry.signal_type ?? entry.signal ?? entry.type ?? entry.category;
      if (typeof tag === 'string') return tag.toLowerCase() === signal;
      if (Array.isArray(tag)) {
        return tag.some((value) => typeof value === 'string' && value.toLowerCase() === signal);
      }
      return false;
    });

    if (matchingItems.length > 0) return evidenceText(matchingItems);
    return getActiveContactComplianceSignals(record).length === 1 ? evidenceText(evidence) : null;
  }

  if (typeof evidence !== 'object') return evidenceText(evidence);
  return evidenceText((evidence as Record<string, unknown>)[signal]);
}

export interface ContactComplianceSummary {
  flaggedCalls: number;
  candidateCalls: number;
  neverSolicited: number;
  dncRequests: number;
  repeatContactComplaints: number;
}

export function summarizeContactCompliance(
  rows: ContactComplianceRecord[]
): ContactComplianceSummary {
  const flagged = new Set<string>();
  const candidates = new Set<string>();
  const bySignal: Record<ContactComplianceSignal, Set<string>> = {
    never_solicited: new Set<string>(),
    dnc_request: new Set<string>(),
    repeat_contact_complaint: new Set<string>(),
  };

  rows.forEach((row, index) => {
    const identity = row.id === null || row.id === undefined ? `row:${index}` : String(row.id);
    const active = getActiveContactComplianceSignals(row);
    if (active.length > 0) flagged.add(identity);
    if (row.contact_compliance_assessment_status === 'candidate') {
      candidates.add(identity);
    }
    active.forEach((signal) => bySignal[signal].add(identity));
  });

  return {
    flaggedCalls: flagged.size,
    candidateCalls: candidates.size,
    neverSolicited: bySignal.never_solicited.size,
    dncRequests: bySignal.dnc_request.size,
    repeatContactComplaints: bySignal.repeat_contact_complaint.size,
  };
}

export interface CampaignComplianceException extends ContactComplianceSummary {
  campaign: string;
  totalCalls: number;
  flaggedRate: number;
}

export function buildCampaignComplianceExceptions(
  rows: ContactComplianceRecord[],
  limit = 10
): CampaignComplianceException[] {
  const campaigns = new Map<
    string,
    {
      all: Set<string>;
      flagged: Set<string>;
      candidates: Set<string>;
      bySignal: Record<ContactComplianceSignal, Set<string>>;
    }
  >();

  rows.forEach((row, index) => {
    const campaign = row.Campaign?.trim() || 'Unknown';
    const identity = row.id === null || row.id === undefined ? `${campaign}:row:${index}` : String(row.id);
    const active = getActiveContactComplianceSignals(row);
    const wasAssessed = Boolean(row.contact_compliance_scored_at) || active.length > 0;
    if (!wasAssessed) return;
    const entry = campaigns.get(campaign) ?? {
      all: new Set<string>(),
      flagged: new Set<string>(),
      candidates: new Set<string>(),
      bySignal: {
        never_solicited: new Set<string>(),
        dnc_request: new Set<string>(),
        repeat_contact_complaint: new Set<string>(),
      },
    };

    entry.all.add(identity);
    if (active.length > 0) entry.flagged.add(identity);
    if (row.contact_compliance_assessment_status === 'candidate') {
      entry.candidates.add(identity);
    }
    active.forEach((signal) => entry.bySignal[signal].add(identity));
    campaigns.set(campaign, entry);
  });

  return Array.from(campaigns.entries())
    .map(([campaign, entry]) => ({
      campaign,
      totalCalls: entry.all.size,
      flaggedCalls: entry.flagged.size,
      candidateCalls: entry.candidates.size,
      flaggedRate:
        entry.all.size > 0 ? Math.round((entry.flagged.size / entry.all.size) * 1000) / 10 : 0,
      neverSolicited: entry.bySignal.never_solicited.size,
      dncRequests: entry.bySignal.dnc_request.size,
      repeatContactComplaints: entry.bySignal.repeat_contact_complaint.size,
    }))
    .filter((entry) => entry.flaggedCalls > 0)
    .sort(
      (left, right) =>
        right.flaggedCalls - left.flaggedCalls ||
        right.flaggedRate - left.flaggedRate ||
        left.campaign.localeCompare(right.campaign)
    )
    .slice(0, limit);
}
